<!-- eslint-disable vue/no-v-html -->
<template>
	<b-container
		fluid
		class="main-container">
		<div class="secondary-container">
			<h1
				class="product-name header"
				v-html="routineTimelineText.title" />
			<img
				v-if="routineSrc"
				:src="requireAsset(routineSrc)"
				alt="Timeline"
				class="product-image img-fluid header">
			<p
				class="product-description"
				v-html="routineTimelineText.description" />
		</div>

		<b-container
			fluid
			:style="gradientBgStyle"
			class="bottle-row-container p-0">
			<img
				v-if="leftBottleSrc"
				:src="requireAsset(leftBottleSrc)"
				alt="Left bottle"
				class="bottle bottle--left">
			<img
				v-if="rightBottleSrc"
				:src="requireAsset(rightBottleSrc)"
				alt="Right bottle"
				class="bottle bottle--right">

			<div class="secondary-container secondary-container--wider">
				<div class="powered-header-container">
					<h1
						class="product-name bolder-text"
						v-html="routineTimelineText.poweredByTitle" />
				</div>
				<p
					class="product-description"
					v-html="routineTimelineText.poweredByDescription" />
			</div>
		</b-container>

		<section class="container my-5">
			<div class="row">
				<!-- LEFT: AM Essentials -->
				<div class="col-lg-6 text-start pe-lg-6 text-justify border-lg-end">
					<h1
						class="bolder-text text-am mb-2"
						v-html="routineTimelineText.left.title" />
					<h2
						class="bolder-text h4 mb-3 text-pm"
						v-html="routineTimelineText.left.subtitle" />

					<p
						class="mb-3"
						v-html="routineTimelineText.left.description" />

					<ul class="list-unstyled mb-4">
						<li
							v-for="(bullet, i) in routineTimelineText.left.bullets"
							:key="'left-bullet-' + i"
							class="d-flex align-items-start mb-2">
							<span class="me-4 text-am">●&nbsp;</span>
							<span v-html="bullet" />
						</li>
					</ul>

					<div>
						<div
							class="bolder-text"
							v-html="routineTimelineText.left.keyIngredientsLabel" />
						<div
							class="text-muted"
							v-html="routineTimelineText.left.keyIngredients" />
					</div>
				</div>

				<!-- RIGHT: PM Essentials -->
				<div class="col-lg-6 text-start text-justify split-col-right">
					<h1
						class="bolder-text mb-2"
						v-html="routineTimelineText.right.title" />
					<h2
						class="bolder-text h4 mb-3"
						v-html="routineTimelineText.right.subtitle" />

					<p
						class="mb-3"
						v-html="routineTimelineText.right.description" />

					<ul class="list-unstyled mb-4">
						<li
							v-for="(bullet, i) in routineTimelineText.right.bullets"
							:key="'right-bullet-' + i"
							class="d-flex align-items-start mb-2">
							<span class="me-2 text-pm">●&nbsp;</span>
							<span v-html="bullet" />
						</li>
					</ul>

					<div>
						<div
							class="bolder-text"
							v-html="routineTimelineText.right.keyIngredientsLabel" />
						<div
							class="text-muted"
							v-html="routineTimelineText.right.keyIngredients" />
					</div>
				</div>
			</div>
		</section>
	</b-container>
</template>

<script>
import { JnsLandingPages } from '@/translations';
import Storage from '@/views/JnsLandingPages/mixins/Storage';
import { PRODUCT_PAGES } from '@/settings/JnsLandingPages';
import Page from '@/views/JnsLandingPages/mixins/Page';
import Replacements from '@/views/JnsLandingPages/mixins/Replacements';

export default {
	name: 'RoutineTimeline',
	messages: [JnsLandingPages],
	mixins: [Page, Storage, Replacements],
	computed: {
		routineTimeline() {
			return PRODUCT_PAGES[this.pageName].routineTimeline || {};
		},
		translateText() {
			return (key) => this.applyReplacements(this.translate(key || ''));
		},
		translateList() {
			return (keys = []) => keys.map((key) => this.translateText(key)).filter(Boolean);
		},
		routineTimelineText() {
			const rt = this.routineTimeline || {};

			return {
				// Top block
				title: this.translateText(rt.title),
				description: this.translateText(rt.description),

				// Gradient (powered by…)
				poweredByTitle: this.translateText(rt.powered_by_title),
				poweredByDescription: this.translateText(rt.powered_by_description),

				// Left (AM) column
				left: {
					title: this.translateText(rt.left?.title),
					subtitle: this.translateText(rt.left?.subtitle),
					description: this.translateText(rt.left.description),
					bullets: this.translateList(rt.left?.bullets),
					keyIngredientsLabel: this.translateText(rt.left?.keyIngredientsLabel),
					keyIngredients: this.translateText(rt.left?.keyIngredients),
				},

				// Right (PM) column
				right: {
					title: this.translateText(rt.right?.title),
					subtitle: this.translateText(rt.right?.subtitle),
					description: this.translateText(rt.right.description),
					bullets: this.translateList(rt.right?.bullets),
					keyIngredientsLabel: this.translateText(rt.right?.keyIngredientsLabel),
					keyIngredients: this.translateText(rt.right?.keyIngredients),
				},
			};
		},
		gradientBgFile() {
			return this.routineTimeline.gradient_bg || '';
		},
		gradientBgStyle() {
			return {
				backgroundImage: `url(${this.requireAsset(this.gradientBgFile)})`,
			};
		},
		routineSrc() {
			return this.routineTimeline.timeline || '';
		},
		leftBottleSrc() {
			return this.routineTimeline.left_bottle || '';
		},
		rightBottleSrc() {
			return this.routineTimeline.right_bottle || '';
		},
	},
};
</script>

<style scoped>
.main-container {
	text-align: center;
	width: 100%;
}

.header {
	font-weight: bold;
	margin-bottom: 2.5rem;
}

.product-description {
	font-size: 18px;
}

.product-image {
	display: block;
	margin: 0 auto 1.5rem auto;
	max-width: 100%;
	height: auto;
}

.bottle-row-container {
	/* full-bleed */
	width: 100vw;
	margin-left: calc(50% - 50vw) !important;
	margin-right: calc(50% - 50vw) !important;

	/* clipping */
	position: relative;

	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
}

/* Center over the gradient */
.bottle-row-container .secondary-container {
	position: relative;
	max-width: min(100ch, 90%);
	margin: 0 auto;
	padding: clamp(1.5rem, 4vw, 4rem) 1rem;
	text-align: center;
}

/* Reset the outer .secondary-container that wraps the top content */
.secondary-container {
	margin: 5% 250px;
}

.secondary-container--wider {
	margin: 5% auto;
	max-width: 99%;
}

.bolder-text {
	font-weight: bolder;
}

/* AM/PM color utilities */
.text-am {
	color: var(--jlp-primary-color);
}
.text-pm {
	color: var(--jlp-secondary-color);
}

.bottle {
	position: absolute;
	top: 50%;
	transform: translateY(-62%) scale(0.90);
	height: auto;
	pointer-events: none;
	user-select: none;
	z-index: 1000;
}

.bottle--left  { left:  max(-8vw, -50px); }
.bottle--right { right: max(-8vw, -50px); transform: translateY(-55%) scale(0.90); }

.powered-header-container {
	padding: 0 10%;
	margin-bottom: 5%;
}

.container {
	margin-top: 5%;
}

@media (min-width: 992px) {
	.split-col-left {
		padding-right: 6rem;   /* space between left text and the line */
	}
	.split-col-right {
		padding-left: 6rem;    /* space between the line and right text */
	}
	.border-lg-end {
		border-right: 2px solid black;
		padding-right: 6rem; /* space between text and divider */

	}
	.col-lg-6.ps-lg-5 {
		padding-left: 6rem; /* space between divider and right column text */
	}
}

@media (max-width: 992px) {
	.split-col-left {
		padding-bottom: 6rem;
	}

	.split-col-right {
		padding-top: 6rem;
	}

	.border-lg-end {
		border-bottom: 2px solid black;
		padding-bottom: 6rem;
	}

	.col-lg-6.ps-lg-5 {
		padding-top: 6rem;
	}

	.secondary-container--wider { max-width: 95%; }

	.bottle-row-container { min-height: clamp(300px, 55vw, 520px); }
	.bottle { height: 100%; opacity: 0.1; }
	.bottle--left  { left:  -5vw; }
	.bottle--right { right: -5vw; }

	.secondary-container {
		margin: 5%;
	}
}

@media (max-width: 1500px) {
	.secondary-container {
		margin: 5%;
	}

	.bottle {
		opacity: 0.1;
	}
}

@media (max-width: 768px) {
	.bottle-row-container { min-height: clamp(300px, 55vw, 520px); }
	.bottle { height: 100%; opacity: 0.1; }
	.bottle--left  { left:  -5vw; }
	.bottle--right { right: -5vw; }
}

@media (max-width: 480px) {
	.bottle { opacity: 0.1; }
	.bottle--left  { left:  -28vw; }
	.bottle--right { right: -28vw; }
}
</style>
